import { KeyboardEvent } from 'react';
import { flushSync } from 'react-dom';

/**
 * Keyboard handling shared by every dropdown menu, attached to the element
 * that holds both its trigger (the element with `aria-expanded`) and its
 * items (`role="menuitem"`):
 *
 * - Down and Up move between the items, wrapping around; Home and End jump to
 *   the first and last one.
 * - From the closed trigger, Down and Up open the menu on its first or last
 *   item.
 * - Escape closes the menu and hands the focus back to the trigger, since the
 *   items it was on are hidden with the menu.
 *
 * `itemSelector` picks other items, such as the checkboxes of a filter panel.
 */
export function onDropdownMenuKey(
  isOpen: boolean,
  setOpen: (open: boolean) => void,
  itemSelector = '[role="menuitem"]'
) {
  return (event: KeyboardEvent<HTMLElement>) => {
    const container = event.currentTarget;

    if (event.key === 'Escape') {
      if (!isOpen) return;

      setOpen(false);
      if (container.contains(document.activeElement)) {
        container.querySelector<HTMLElement>('[aria-expanded]')?.focus();
      }
      return;
    }

    const opensMenu = event.key === 'ArrowDown' || event.key === 'ArrowUp';
    const isNavigationKey =
      opensMenu || event.key === 'Home' || event.key === 'End';
    if (!isNavigationKey || (!isOpen && !opensMenu)) return;

    event.preventDefault();
    if (!isOpen) {
      // The items only become visible once the menu renders open.
      flushSync(() => setOpen(true));
    }

    const items = Array.from(
      container.querySelectorAll<HTMLElement>(itemSelector)
    );
    if (items.length === 0) return;

    const last = items.length - 1;
    const current = items.findIndex((item) => item === document.activeElement);
    let next = 0;
    switch (event.key) {
      case 'ArrowDown':
        next = current >= last ? 0 : current + 1;
        break;
      case 'ArrowUp':
        next = current <= 0 ? last : current - 1;
        break;
      case 'End':
        next = last;
        break;
    }
    items[next].focus();
  };
}
